"use client";

import { FormEvent, useState } from "react";

type SafeContract = {
  id: string;
  status: string;
  plano: string;
  vencimento: string;
  cidade?: string;
};

type ClientData = {
  documento: string;
  cpfcnpj?: string;
  contratos: SafeContract[];
};

type ApiResponse = {
  data?: {
    found?: boolean;
    documento?: string;
    contratos?: SafeContract[];
  };
  error?: {
    code: string;
    message: string;
  };
};

function formatDocumentInput(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 14);

  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, "$1.$2")
      .replace(/^(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
  }

  return digits
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3/$4")
    .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, "$1.$2.$3/$4-$5");
}

export function CustomerAccessForm({ onSuccess }: { onSuccess?: (data: ClientData) => void }) {
  const [cpfcnpj, setCpfcnpj] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [contracts, setContracts] = useState<SafeContract[]>([]);

  async function accessByDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setStatus("loading");
    setMessage("Consultando contrato no SGP...");
    setContracts([]);

    try {
      const response = await fetch("/api/sgp/cliente/buscar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ cpfcnpj }),
      });
      const payload = (await response.json()) as ApiResponse;

      if (!response.ok || payload.error) {
        throw new Error(payload.error?.message ?? "Não foi possível consultar o cliente.");
      }

      if (!payload.data?.found) {
        setStatus("error");
        setMessage(`Nenhum contrato encontrado para ${payload.data?.documento ?? "o documento informado"}.`);
        return;
      }

      const resultContracts = Array.isArray(payload.data.contratos)
        ? payload.data.contratos
        : [];

      setStatus("success");
      setContracts(resultContracts);
      setMessage(
        `${resultContracts.length} contrato(s) liberado(s) para ${payload.data.documento}.`,
      );
      onSuccess?.({
        documento: payload.data.documento ?? cpfcnpj,
        cpfcnpj: cpfcnpj.replace(/\D/g, ""),
        contratos: resultContracts,
      });
    } catch (error) {
      setStatus("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "Falha ao consultar contrato no SGP.",
      );
    }
  }

  const feedbackBg =
    status === "success"
      ? "rgba(6,214,160,.08)"
      : status === "error"
        ? "rgba(239,68,68,.08)"
        : "rgba(96,165,250,.08)";
  const feedbackColor =
    status === "success" ? "#34d399" : status === "error" ? "#f87171" : "#60a5fa";

  return (
    <div
      className="hn-form-wrap"
      style={{ textAlign: "left" }}
    >
      <form style={{ display: "flex", flexDirection: "column", gap: 16 }} onSubmit={accessByDocument}>
        <label className="block">
          <span className="hn-form-label" style={{
            display: "block",
            textAlign: "center",
            fontSize: 18,
            fontWeight: 800,
            background: "linear-gradient(90deg, #60a5fa, #2563eb)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            marginBottom: 8,
            letterSpacing: "-.2px",
          }}>CPF ou CNPJ</span>
          <input
            className="hn-form-input"
            style={{ fontSize: 20, height: 60, textAlign: "center", letterSpacing: "0.12em" }}
            inputMode="numeric"
            onChange={(event) => setCpfcnpj(formatDocumentInput(event.target.value))}
            placeholder=""
            value={cpfcnpj}
          />
        </label>
        <button
          className="hn-btn-primary"
          style={{ height: 56, fontSize: 16 }}
          disabled={status === "loading"}
          type="submit"
        >
          {status === "loading" ? "Consultando..." : "Acessar meu contrato"}
        </button>
      </form>

      <p style={{ fontSize: 12, color: "#5a7aa0", marginTop: 12, lineHeight: 1.5 }}>
      </p>

      {message ? (
        <div
          className="hn-feedback"
          style={{ background: feedbackBg, border: `1px solid ${feedbackColor}30`, marginTop: 12 }}
          role="status"
          aria-live="polite"
        >
          <p style={{ fontWeight: 700, color: feedbackColor }}>{message}</p>
          {contracts.length > 0 ? (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
              {contracts.map((contract) => (
                <div key={contract.id} className="hn-feedback-contract">
                  <p className="hn-feedback-plan">{contract.plano}</p>
                  <p className="hn-feedback-meta">
                    Contrato {contract.id} · {contract.status} · vencimento {contract.vencimento}
                  </p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
